import qs from "query-string";
import axios from "axios/index";
import Color from "color";
import * as Sentry from "@sentry/react";
import ReactDOM from "react-dom";

import {
  actionTypes,
  chatOutOfBudgetUrl,
  getActiveModule,
  getBrandColor,
  getChatButtonBackground,
  getChatUpdateUrl,
  getFontFamily,
  getFontSize,
  getJobApplicantUrl,
  getJobApplicationSubmitted,
  getLeadId,
  getMessagesHistory,
  getModuleById,
  getTeaserShownTimes,
  getTextColor,
  getUserID,
  getVisitorID,
  getVisitorUpdateUrl,
} from "./store";

import {
  leadIDKey,
  localhost,
  nextPageUrlKey,
  savedStateKey,
  ScreenSize,
  teaserClosedKey,
  teaserShownTimesKey,
  UUIDKey,
} from "./lib/constants";

import chatConfigExample from "./configExamples/chatConfigExample";
import seleniumChatConfigExample from "./configExamples/seleniumChatConfigExample";
import personalizedMessagesConfigExample from "./configExamples/personalizedMessages";
import weirdTestConfigExample from "./configExamples/weirdTest";
import chatConfigInteractiveMessages from "./configExamples/interactiveMessagesConfigExample";

import { urlConfig, urlLocation } from "./lib/utils";
import type { Module } from "./lib/types";
import { ChatModes, MessageTypes, ModuleRoles, ModuleTypes } from "./lib/types";
import { UserEventsAPI } from "./lib/userEventsAPI";
import { initIAstart } from "./Components/InstantAnswers/actions";
import { queryLocations } from "./Components/Locations/actions";
import {
  addMessage,
  changeEditingMessage,
  changeTextInput,
  makeMessagesUneditable,
} from "./Components/messages/actions";
import { saveModuleBeforeGallery } from "./Components/Gallery/actions";
import { deactivateSlackModule, initSlackModule } from "./Components/SlackController/actions";
import { getWorkWithHistory } from "./Components/messages/selectors";
import API from "./lib/api";

/**
 * Obtain UUID for the user from the server.
 */
export const loadUUID = () => async (dispatch, getState) => {
  // History of experiments.
  // Please when you set up a new experiment here - put the old one at the end of the list here.

  // james-mobile-redesign
  // scroll-fix
  // scroll-fix-2
  // test-redesign-1-B
  // test-redesign-custom-events
  // gallery-fixes
  // editable-field // ended at 18.10.2020

  // const experimentName = window.famExperimentName || "test-redesign-custom-events";
  const experimentName = "cross-site-tracking";

  const HTFrmSrcKeyword =
    document &&
    document.cookie
      .split("; ")
      .filter((x) => x.includes("HTFrmSrcKeyword="))[0]
      ?.replace("HTFrmSrcKeyword=", "");

  const browserData = {
    experimentName,
    screenWidth: screen.width,
    screenHeight: screen.height,
    innerWidth,
    innerHeight,
    referrer: document.referrer,
    urlParameters: urlConfig,
    urlLocation: urlLocation.href, // Because url parameters object won't include the domain e.g.
    HTFrmSrcKeyword,
  };

  if (urlConfig.staticConfig || urlConfig.seleniumConfig) {
    return null;
  }
  // Request new user ID.
  const response = await dispatch(API.startChat({ browserData }));

  const { uuid } = response.data;

  dispatch(deepChange({ uuid }));
  dispatch(linkChatUserToVisitor());

  localStorage.setItem(UUIDKey, uuid);

  dispatch(UserEventsAPI.flushEvents());
};

/**
 * An important initializer:
 * Called after the chat config was received from the server.
 * Stores that config in the store and
 * call few more consequtive initializers.
 */
export const setChatConfig = (config) => (dispatch, getState) => {
  const state = getState();

  dispatch({
    type: actionTypes.SET_CONFIG,
    payload: config,
  });

  const firstModuleAnswers = getModuleById(state, 1).answers;

  if (typeof config.activeModule === "object" && Object.keys(config.activeModule).length !== 0) {
    dispatch(setActiveModule(config.activeModule));
  }
  if (firstModuleAnswers && firstModuleAnswers.length <= state.maxAnswersToInitiallyShow) {
    dispatch(deepChange({ showAllAnswerOptions: true }));
  }

  if (!state.uuid) {
    dispatch(loadUUID());
  }

  dispatch(UserEventsAPI.sendPageView());

  dispatch(initWindowAgent());

  if (config.config.customJavascript) {
    try {
      eval(config.config.customJavascript);
    } catch (e) {
      console.log("FamilyAssets Error: eval(config.customJavascript) failed:");
      console.log(e);
    }
  }
};

/**
 * Handler that gets registered at `window.FamilyAssetsChat` to provide an external API for the chat.
 */
class Agent {
  constructor(dispatch, getState) {
    this.dispatch = dispatch;
    this.getState = getState;
    if (urlConfig.localTesting) {
      this.deepChange = deepChange;
    }
    this.API = UserEventsAPI;
  }

  close = () => {
    this.dispatch(deepChange({ chatMode: ChatModes.circle }));
  };

  open = () => {
    window._famchat.openChat();
  };

  /**
   * Retrieve lead info to be submitted.
   * Used in selenium tests instead of a real lid submission.
   */
  getLeadInfo = () => {
    const state = this.getState();
    return getSubmitLeadData(state);
  };

  /**
   * FamilyAssets go to Module.
   * Used in chat_edit page.
   */
  gotoModule = (module) => {
    this.dispatch(goToModule(module));
  };
}

/**
 * Create new `Agent` instance and assign it to `window.FamilyAssetsChat`.
 * @returns {Function}
 */
const initWindowAgent = () => (dispatch, getState) => {
  window.FamilyAssetsChat = new Agent(dispatch, getState);
};

/**
 * Converts the get?parameters=1 into an object.
 */
export const loadUrlConfig = () => (dispatch) => {
  const { hackPageHeight } = urlConfig;
  if (hackPageHeight) {
    document.querySelector(".html-content")?.setAttribute("style", "height: 100%; box-sizing: border-box;");
    document.querySelector("body")?.setAttribute("style", "margin: 0;");
  }
};

/**
 * Create an invisible IFrame, load specific familyassets page in it to get the user's session data - the `Visitor` uuid.
 * (See the `Visitor` model in the burgundy repo for details
 * Also check if user his permission to save data back to the backend.
 */
export const checkFamilyAssetsSession = () => (dispatch) => {
  const iframe = document.createElement("iframe");
  let domain = "https://www.familyassets.com";
  if (urlConfig.localConfig) {
    domain = `http://${localhost}:8000`;
  }
  iframe.setAttribute("src", `${domain}/user/login_iframe`);
  iframe.setAttribute("style", "position: absolute; width: 0; height: 0; top: -10px; left: -10px;");

  window.addEventListener("message", receiveMessage, false);

  function receiveMessage(event) {
    const localDevelopmentDomains = [`http://${localhost}:3000`];
    const apiDomains = [
      `http://${localhost}:8000`,
      "https://www.familyassets.com",
      "http://localfamilyassets.com:3000", // For development.
    ];
    if (![...localDevelopmentDomains, ...apiDomains].includes(event.origin)) {
      // console.error(`receiveMessage: Received a message from wrong origin: ${event.origin}`);
      return;
    }
    if (apiDomains.includes(event.origin)) {
      // Because there also could be messages from Webpack during development or from some browser extensions
      // during production.
      // console.log("message received:", event.data);
      const { superUser, visitorUUID } = event.data;
      // console.log("familyAssets iframe authorized:", event.data);
      if (superUser) {
        dispatch(deepChange({ familyAssetsAdmin: Boolean(superUser) }));
      }
      if (visitorUUID) {
        dispatch(deepChange({ visitorUUID }));
        dispatch(linkChatUserToVisitor());
      }
    }
  }

  ReactDOM.findDOMNode(window.famIframeContainerRef).appendChild(iframe);
};

/**
 * Called after receiving Visitor UUID and ChatUser UUID.
 * When both of them become available - makes another request
 * to the backend to link them together.
 */
export const linkChatUserToVisitor = () => (dispatch, getState) => {
  const state = getState();
  const visitorID = getVisitorID(state);
  const userID = getUserID(state);

  if (visitorID && userID) {
    // Few extra jumps allow us to use request.POST.get() in django later.
    axios
      .post(getVisitorUpdateUrl(state), qs.stringify({ visitorID, userID }), {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      })
      .then(
        (e) => console.log(e),
        (e) => console.log(e)
      );
  }
};

/**
 * Return API domain.
 * @returns {string}
 */
const getApiDomain = () => {
  if (urlConfig.localConfig) {
    return `${localhost}:8000`;
  }
  return "www.familyassets.com";
};

/**
 * Return the Http API domain string.
 * @returns {string}
 */
export const getHttpApiAddress = () => {
  if (urlConfig.localConfig) {
    return `http://${getApiDomain()}`;
  }
  return `https://${getApiDomain()}`;
};

/**
 * Return websocket address.
 * @returns {string}
 */
export const getWebsocketAddress = (dispatch, getState) => {
  const state = getState();
  if (urlConfig.localConfig) {
    return `ws://${getApiDomain()}/ws/chat/${getUserID(state)}/`;
  }
  return `wss://ws.familyassets.com/ws/chat/${getUserID(state)}/`;
};

/**
 * Do an ajax call fot the chat config.
 * First - figure out which domain to use based on the get parameters.
 */
export const loadChatConfig = () => (dispatch, getState) => {
  let defaultDomainName = "familyassets.com";
  let loadConfigFrom = "https://www.familyassets.com";
  // let loadConfigFrom = 'http://localhost';
  if (urlConfig.clearStorage) {
    localStorage.clear();
  }
  if (urlConfig.localConfig) {
    loadConfigFrom = getHttpApiAddress();
  } else if (urlConfig.staticConfig) {
    dispatch(setChatConfig(chatConfigExample.data));
    return;
  } else if (urlConfig.seleniumConfig) {
    const config = seleniumChatConfigExample;
    if (urlConfig.calendarTesting) {
      config.config = {
        ...config.config,
        timesAvailable: {
          3: [
            [9, 11],
            [14, 15],
          ],
        },
        timezoneName: "US/Pacific",
        timezoneOffset: "-07:00",
      };
    }
    dispatch(setChatConfig(config));
    return;
  } else if (urlConfig.personalizedMessages) {
    dispatch(setChatConfig(personalizedMessagesConfigExample));
    return;
  } else if (urlConfig.interactiveMessages) {
    dispatch(setChatConfig(chatConfigInteractiveMessages));
    return;
  } else if (urlConfig.weirdTest) {
    dispatch(setChatConfig(weirdTestConfigExample));
    return;
  }
  if (urlConfig.simulateDomain) {
    defaultDomainName = urlConfig.simulateDomain;
  }
  const domain = window.familyAssetsChatUrl || defaultDomainName;
  let query = `url=${domain}`;
  if (window.FamilyAssetsLanguage) {
    query += `&lang=${window.FamilyAssetsLanguage}`;
  }
  if (window.FamilyAssetsStrictURL) {
    query += "&strict=True";
  }
  if (window.familyAssetsChatProviderProfile) {
    query += `&provider_profile_id=${window.familyAssetsChatProviderProfile}`;
  }
  if (urlConfig.providerProfileId) {
    query += `&provider_profile_id=${urlConfig.providerProfileId}`;
  }
  axios
    .get(`${loadConfigFrom}/api/chat/config?${query}`)
    .then((response) => {
      dispatch(setChatConfig(response.data.data));
    })
    .catch((e) => {});
};

/**
 * Play bot message sound if `teaser` argument is not equal to the current `chatOpen` flag.
 * @returns {Function}
 */
export const playBotMessageSound = () => (dispatch, getState) => {
  const state = getState();
  // if (!getIsChatOpen(state) && !state.isTeaserVisible) {
  //   return;
  // }
  state.botMessageSound.play();
};

/**
 * Do an ajax call to store an answer for the current module to the server.
 */
export const sendCurrentModuleAnswer = (answer) => (dispatch, getState) => {
  const state = getState();
  const question_id = getActiveModule(state).id;
  dispatch(
    deepChange({
      path: "conversation",
      value: [...state.conversation, { question_id, answer }],
    })
  );
  dispatch(sendModuleAnswer(question_id, { text: answer, id: null }));
};

/**
 * Send ajax call with an answer to a module by the module's ID.
 */
export const sendModuleAnswer = (question_id, answer) => (dispatch, getState) => {
  // Not producing useless errors when debugging or developing.
  if (urlConfig.staticConfig) {
    return;
  }
  const state = getState();

  // Wrap everything with an array for the consistency - multi-option question requires an array here.
  if (!Array.isArray(answer)) {
    answer = [answer];
  }

  axios
    .post(getChatUpdateUrl(state), {
      uuid: state.uuid,
      question_id,
      answer,
    })
    .catch((response) => {
      Sentry.withScope(function (scope) {
        Sentry.captureException(response);
      });
      // console.log(response);
    });
};

/**
 * Save most important parts of the state into the local storage to load it back
 * once the user navigates to another page or visits current site again.
 */
const saveState = () => (dispatch, getState) => {
  const state = getState();

  const { activeModule, modules, messagesHistory, localHistoryLoaded, leadID, uuid, userData, conversation } = state;

  localStorage.setItem(
    savedStateKey,
    JSON.stringify({
      activeModule,
      modules,
      messagesHistory,
      localHistoryLoaded,
      leadID,
      uuid,
      userData,
      conversation,
    })
  );
};

/**
 * Start page title flashing.
 * @returns {Function}
 */
export const initiateTitleFlashing = () => (dispatch, getState) => {
  setTimeout(() => {
    const originalPageTitle = window.document.title;
    const customTitle = "New Message!";
    // Need to flash a finite number of times for jest to be able to mock the timers.
    let flashedTimes = 0;
    const titleFlashingInterval = setInterval(() => {
      window.document.title = window.document.title === originalPageTitle ? customTitle : originalPageTitle;
      flashedTimes += 1;
      if (flashedTimes >= 50 * 1000) {
        dispatch(stopTitleFlashing());
      }
    }, 1200);
    dispatch(deepChange({ originalPageTitle, titleFlashingInterval }));
  }, 20000);
};

/**
 * Stop page title flashing.
 * @returns {Function}
 */
export const stopTitleFlashing = () => (dispach, getState) => {
  const state = getState();
  if (state.titleFlashingInterval !== null) {
    clearInterval(state.titleFlashingInterval);
    dispach(deepChange({ titleFlashingInterval: null }));
  }
  window.document.title = state.originalPageTitle;
};

/**
 * Trying out some hacks here.
 * An extension for the Module class which keeps it a NOT ES-6 class instance, but adds some
 * handy methods to it.
 * Assignment methods work only when the module is the currently active one.
 * TODO: add a validation of this.
 * Lives in actions.js because it uses the `deepChange` action and actions use this mixin -
 * if place them in a different files - the are circular import eslint error
 * even though it still build completely fine.
 * @type {{}}
 */
export const ModuleExtensionMixin = {
  /**
   * Lets just as well store it here.
   */
  activeFloor: 0,

  isGallery(): boolean {
    return this.role === ModuleRoles.gallery;
  },

  isInChatGallery(): boolean {
    return this.role === ModuleRoles.inChatGallery;
  },

  isInChatFloorplan(): boolean {
    return this.role === ModuleRoles.inChatFloorplan;
  },

  isInChatDigitalTour(): boolean {
    return this.role === ModuleRoles.inChatDigitalTour;
  },

  isFloorplan(): boolean {
    return this.role === ModuleRoles.floorplan;
  },

  isAdvancedFloorplan(): boolean {
    return this.role === ModuleRoles.advancedFloorplan;
  },

  isVideo(): boolean {
    return this.role === ModuleRoles.video;
  },

  isDigitalTour(): boolean {
    return this.role === ModuleRoles.digitalTour;
  },

  /**
   * Tell if the gallery should be currently visible.
   * This is based solely on the module properties.
   * Can be simplified to not check for the youtube/iframe role, but now it's more
   * explicit.
   * @returns {*|boolean}
   */
  shouldShowGallery(): boolean {
    return (
      this.isGallery() || this.isFloorplan() || this.isDigitalTour() || this.isVideo() || this.isAdvancedFloorplan()
    );
  },
};

export const setActiveModule = (moduleData) => (dispatch) => {
  const extendedModule = {
    ...moduleData,
    ...ModuleExtensionMixin,
    dispatch,
  };
  dispatch(deepChange({ activeModule: extendedModule }));
};

/**
 * Go to another module by it's ID. If ID is null - close the chat.
 * One of the most complicated parts of the code -
 * does different stuff for different module types.
 * @param moduleID - ID of the new module.
 * @param quiet - if set - do not produce any messages while switching to a new module.
 * @param oldModule - if this module was already counted.
 */
export const goToModule = (moduleID, { quiet = false, oldModule = false } = {}) => (dispatch, getState) => {
  const state = getState();
  const prevModule = getActiveModule(state);
  const workWithHistory = getWorkWithHistory(state);

  if (Number.isInteger(moduleID) && Object.keys(state.modules).includes(moduleID.toString())) {
    const newModule: Module = getModuleById(state, moduleID);
    dispatch(setActiveModule({ ...newModule, id: moduleID }));

    if (newModule.type === ModuleTypes.submit) {
      if (newModule.text) {
        try {
          eval(newModule.text);
        } catch (e) {
          console.log("FamilyAssets Error: eval(submit.text) failed:");
          console.log(e);
        }
      }
      dispatch(submitLead());
      dispatch(goToModule(newModule.action));
    } else if (newModule.type === ModuleTypes.submitJobApplicant) {
      dispatch(submitJobApplication());
      dispatch(goToModule(newModule.action));
    } else if (newModule.type === ModuleTypes.sms) {
      dispatch(submitLead());
      dispatch(goToModule(newModule.action));
    } else if (newModule.type === ModuleTypes.link) {
      // Saving the state before leaving page.
      localStorage.setItem(nextPageUrlKey, newModule.link_href);
      window.open(newModule.link_href, "_blank");
      dispatch(goToModule(newModule.action)).then(() => dispatch(saveState()));
    } else if (newModule.type === ModuleTypes.click) {
      document.querySelector(newModule.link_href).click();
      dispatch(goToModule(newModule.action));
      // Design does not allow us to hijack full screen on mobile.
      if (window.innerWidth < ScreenSize.mobile) {
        dispatch(closeChat());
      }
    } else if (newModule.type === ModuleTypes.eval) {
      eval(newModule.text);
      dispatch(goToModule(newModule.action));
    } else if (newModule.type === ModuleTypes.guide) {
      quiet = true;
    } else {
      const showTextInput = ["text", "phone", "email", "slack"].includes(newModule.type);
      const showDatePicker = newModule.type === ModuleTypes.datePicker;
      const showConfirmSelectionButton = newModule.type === ModuleTypes.multiOption;

      // This condition checks field content at the state.userData object,
      // if the field is filled, skip the current step.
      if (
        ["first_name", "phone", "email", "lead_additional_information"].includes(newModule.key) &&
        !!state.userData[newModule.key] &&
        !getWorkWithHistory(state)
      ) {
        return dispatch(goToModule(newModule.action));
      }

      if (newModule.type === ModuleTypes.slack) {
        dispatch(initSlackModule);
      } else if (state.slackController) {
        dispatch(deactivateSlackModule);
      }

      if (newModule.type === ModuleTypes.outOfBudget) {
        axios.post(chatOutOfBudgetUrl(state), { uuid: state.uuid }).then(
          (e) => console.log(e),
          (e) => console.log(e)
        );
        return dispatch(goToModule(newModule.action));
      }

      if (
        newModule.isGallery() ||
        newModule.isVideo() ||
        newModule.isFloorplan() ||
        newModule.isAdvancedFloorplan() ||
        newModule.isDigitalTour()
      ) {
        dispatch(saveModuleBeforeGallery((prevModule && prevModule.id) || 1));
        quiet = true;
      }

      if (newModule.type === ModuleTypes.instantAnswers) {
        dispatch(initIAstart());
      }

      if (newModule.type === ModuleTypes.locations) {
        dispatch(queryLocations());
      }

      if (showTextInput !== state.showTextInput) {
        dispatch(deepChange({ showTextInput }));
      }
      if (showTextInput && !workWithHistory) {
        // +1 because first add a new bot message and then user inputs his message with the next number.
        dispatch(changeEditingMessage(getMessagesHistory(state).length + (oldModule ? 0 : 1)));
        dispatch(changeTextInput(""));
        // dispatch(setIfWorkingWithHistory(false));
      }
      if (showDatePicker !== state.showDatePicker) {
        dispatch(deepChange({ showDatePicker }));
      }
      if (showConfirmSelectionButton !== state.showConfirmSelectionButton) {
        dispatch(deepChange({ showConfirmSelectionButton }));
      }

      if (!quiet && newModule.type !== ModuleTypes.instantAnswers && newModule.type !== ModuleTypes.locations) {
        dispatch(
          addMessage({
            type: MessageTypes.BOT_MESSAGE,
            id: Number.parseInt(moduleID),
          })
        );
      }
    }
  } else if (moduleID === null) {
    setTimeout(() => {
      dispatch(closeChat());
    }, 10);
  }
  return Promise.resolve();
};

/**
 * A flexible state change action - when I got tired of writting a new action for
 * every small thing.
 * Can be called via {path, value} params or
 * {value, anotherValue} - in this case will use each value's name as a path.
 */
export const deepChange = (param) => (dispatch) => {
  let path;
  let value;
  if (typeof param !== "object") {
    console.error("FAM ERROR: wrong deepChange parameter!");
    return;
  }
  // Allow different ways to pass params;
  // Can be {path: "something", value: ValueObject};
  if (Object.keys(param).includes("path") && Object.keys(param).includes("value")) {
    ({ path, value } = param);
    dispatch({
      type: `${actionTypes.DEEP_CHANGE}_${path}`,
      payload: { path, value },
    });
  } else {
    // Can be just {value, anotherValue};
    Object.entries(param).forEach(([path, value]) => {
      dispatch({
        type: `${actionTypes.DEEP_CHANGE}_${path}`,
        payload: { path, value },
      });
    });
  }
};

/**
 * Make additional ajax calls to our backend after the lead was submitted,
 * depending on the actions the user did in the chat - which modules he has passed
 * NOT TOTALLY BULLETPROOF - imagine if somebody first submitted a tour
 * request, but then decided to also take the "sms"/pricing path.
 */
export const postSubmitLead = (activeModule) => (dispatch, getState) => {
  const state = getState();
  const leadID = getLeadId(state);

  // Distinguish `lead` step for the `price` and `tour` dialogue branches.
  if (state.userData.date && state.userData.tour_time) {
    const hour = state.userData.tour_time.split(":")[0];
    const amPm = state.userData.tour_time.split(" ")[1];
    axios
      .post(`${getHttpApiAddress()}/api/tour/${leadID}`, {
        date: `date-${state.userData.date}`,
        hour: hour.toString(),
        minute: "0",
        am_pm: amPm,
      })
      .catch((response) => {
        Sentry.withScope(function (scope) {
          Sentry.captureException(response);
        });
        // console.log(response);
      });
  } else if (activeModule.type === ModuleTypes.sms) {
    axios.post(`${getHttpApiAddress()}/api/sms/${leadID}`, {}).catch((response) => {
      Sentry.withScope(function (scope) {
        Sentry.captureException(response);
      });
      // console.log(response);
    });
  }

  dispatch(makeMessagesUneditable());
};

/**
 * Compose lead data object to be submitted.
 * @param state
 * @returns {{conversation: *|Array, uuid}}
 */
export const getSubmitLeadData = (state) => {
  const { utm_source, utm_medium, utm_campaign } = urlConfig;
  const utmData = {};
  if (utm_source) {
    utmData.utm_source = utm_source;
  }
  if (utm_medium) {
    utmData.utm_medium = utm_medium;
  }
  if (utm_campaign) {
    utmData.utm_campaign = utm_campaign;
  }

  let result = {
    ...state.userData,
    ...utmData,
    conversation: state.conversation,
    uuid: state.uuid,
  };

  if (typeof window.additionalFamilYAssetsChatData !== "undefined") {
    result = {
      ...result,
      additional_data: window.additionalFamilYAssetsChatData,
    };
  }

  return result;
};

/**
 * Check that current user does not have a leadID in the store (which is also saved in the local storage) -
 * we don't want to have multiple lead for the same session/user.
 * If not - submit the lead data to the server to create a new lead and store it's ID back at the frontend.
 * In both cases - do the stuff which has to be done after the lead is submitted.
 */
export const submitLead = () => async (dispatch, getState) => {
  const state = getState();
  const activeModule = getActiveModule(state);
  // activeModule needs to be calculated here.

  if (urlConfig.noSubmit) {
    const leadID = "fake-lead-id-123";
    dispatch(deepChange({ leadID }));
    localStorage.setItem(leadIDKey, leadID);
  } else if (!getLeadId(state)) {
    axios
      .post(state.config.createURL, getSubmitLeadData(state))
      .then((response) => {
        const leadID = response.data.lead_id;

        dispatch(deepChange({ leadID }));

        localStorage.setItem(leadIDKey, leadID);

        dispatch(postSubmitLead(activeModule));
      })
      .catch((response) => {
        Sentry.withScope(function () {
          Sentry.captureException(response);
        });
      });
  } else {
    try {
      await dispatch(API.resubmitLead());
    } catch (e) {
      Sentry.withScope(function () {
        Sentry.captureException(e);
      });
    }
    dispatch(postSubmitLead(activeModule));
  }
  if (state.weekendInactive && state.isWeekend) {
    dispatch(
      addMessage({
        type: MessageTypes.EXTRA_BOT_MESSAGE,
        text: "As it is the weekend, someone will get back to you within the next business day.",
      })
    );
  }
};

export const openChat = () => (dispatch, getState) => {
  dispatch(deepChange({ chatMode: ChatModes.open, showAllAnswerOptions: true }));
  dispatch(stopTitleFlashing());
};

export const closeChat = () => (dispatch, getState) => {
  dispatch(deepChange({ chatMode: ChatModes.long }));
};

/**
 * Increase the number of times the teaser was shown.
 * The values is stored in the local storage.
 */
export const updateTeaserShownTimes = () => {
  const newValue = getTeaserShownTimes() + 1;
  localStorage.setItem(teaserShownTimesKey, newValue);
};

/**
 * Set a variable in the local storage that user has closed the teaser.
 * @property closedDownTo - the chat state to which it was closed.
 */
export const rememberTeaserClosed = (closedDownTo) => {
  localStorage.setItem(teaserClosedKey, closedDownTo);
};

/**
 * Generic 'add to array' action.
 * Add a new member to the specified array in the store.
 * @param path
 * @param value
 * @returns {Function}
 */
export const addToArray = ({ path, value }) => (dispatch) => {
  dispatch({
    type: actionTypes.ADD_TO_ARRAY,
    payload: {
      path,
      value,
    },
  });
};

/**
 * Get StyleComponent css theme.
 * Simplifies the styles props passing.
 * @param state
 * @returns {{brandColor: *, brandColorRGB: string | *, brandColorDarker: (*|void), noAnimation: *}}
 */
export const getTheme = (state) => {
  const brandColor = getBrandColor(state);
  const brandColorRGB = Color(brandColor).rgb().array().join(", ");
  return {
    brandColor,
    brandColorRGB,
    brandColorDarker: Color(brandColor).darken(0.15).rgb().string(),
    brandColorLighter: Color(brandColor).lighten(0.55).rgb().string(),
    brandColorHalfTransparent: `rgb(${brandColorRGB}, 0.5)`,
    noAnimation: urlConfig.noAnimation,
    fontFamily: getFontFamily(state),
    fontSize: getFontSize(state),
    textColor: getTextColor(state),
    buttonColor: getChatButtonBackground(state),
  };
};

/**
 * Scroll the chat to the bottom.
 * @param timeout
 * @returns {Function}
 */
export const scrollBottom = (timeout = 150) => {
  setTimeout(() => {
    try {
      if (window.famScrollArea) {
        window.famScrollArea.scrollArea.refresh();
        window.famScrollArea.scrollBottom();
      }
    } catch (e) {}
  }, timeout);
};

/**
 * Compose data object to be submitted for job applicant request
 */
export const getSubmitJobApplicationData = (state) => {
  const { first_name, email, phone } = state.userData;
  const full_name = first_name.split(" ");

  const result = {
    first_name: full_name[0],
    last_name: full_name[1],
    email,
    phone,
  };

  return result;
};

/**
 * Fetch state user data for current user and create record for job applicant
 */
export const submitJobApplication = () => async (dispatch, getState) => {
  const state = getState();

  // Prevent sending multiple request with identical data after initial request
  // When flow recognizes state.userData is present it fast forwards trough flow actions
  if (!getJobApplicationSubmitted(state)) {
    await axios
      .post(getJobApplicantUrl(state), getSubmitJobApplicationData(state))
      .then((response) => {
        dispatch(deepChange({ jobApplicationSubmitted: true }));
      })
      .catch((response) => {
        Sentry.withScope(function () {
          Sentry.captureException(response);
        });
      });
  }
};
